/* eslint-disable no-nested-ternary */
import React, {useEffect, useMemo, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Link, useNavigate} from 'react-router-dom'
import {BsFillPersonLinesFill} from 'react-icons/bs'
import {
  FiAlertCircle,
  FiArrowLeft,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
  FiClock,
  FiExternalLink,
  FiFolder,
  FiGitMerge,
  FiMoreVertical,
  FiPrinter,
  FiRotateCcw,
  FiTag,
  FiTrash2,
} from 'react-icons/fi'
import {MdSettingsBackupRestore} from 'react-icons/md'
import ReactToPrint from 'react-to-print'
import {useHotkeys} from 'react-hotkeys-hook'
import moment from 'moment'
import _ from 'lodash'
import {
  VmoButton,
  VmoButtonGroup,
  VmoChipInput,
  VmoDivider,
  VmoDropdown,
  VmoDropdownHeader,
  VmoDropdownItem,
  VmoDropdownMenu,
  VmoIcon,
  VmoLabel,
  VmoRating,
  VmoTooltip,
} from 'vmo-library'

import {
  moveConversationTo,
  permanentDeleteConversation,
  snoozeConversation,
  updateConversation,
  updateConversationAndRefresh,
  updateConversationPriority,
  updateConversationStatus,
} from 'actions/inbox'
import {submitTimeTrackingManual} from 'actions/time_tracking'
import {createTagSettings} from 'actions/settings'
import PermissionIcon from 'components/common/PermissionIcon'
import VmoCustomDropdown from 'components/common/VmoCustomDropdown'
import {get, getPermissionTooltipContent, startCase} from 'utils/helper'
import {priorityList} from 'constants/variables'
import CreateLabel from 'components/common/CreateLabel'
import useUserPermissions from 'hooks/pure/useUserPermission'
import {INBOX_TYPES} from 'constants/enums'
import {INBOX_KEYBOARD_SHORTCUTS} from 'constants/keyboard-shortcuts'
import MergeConversationModal from './MergeConversationModal'
import OverdueModal from './OverdueModal'
import EmailDeleteModal from './EmailDeleteModal'
import SnoozeConversationModal from './SnoozeConversationModal'

const EmailBodyHeader = props => {
  const {
    ticketNo,
    layout,
    conversations,
    conversation,
    ConversationId,
    CurrentStatusOfConversation,
    isSnoozed,
    mailboxes,
    mailboxId,
    CurrentReadStatusOfConversation,
    isTrashedConversation,
    emailBodyContentRef,
    CurrentSpamStatusOfConversation,
    timeTrackingData,
    timeTrackingCounterStatus,
    setTimeTrackingCounterStatus,
    timeTrackingTimer,
    setTimeTrackingTimer,
    CurrentPriorityOfConversation,
    CurrentAssigneeOfConversation,
    currentTeamOfConversation,
    assigneeOptions,
    onClickItem,
    conversationSubject,
    currentLabelsOfConversation,
    customerProfile,
    setCustomerProfile,
  } = props

  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [timePicker, setTimePicker] = useState(false)
  const [snoozeDatePickerTime, setSnoozeDatePickerTime] = useState(new Date())
  const [isMergeModalOpen, setMergeModalOpen] = useState(false)
  const [overDue, setOverDue] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [activeAssigneeType, setActiveAssigneeType] = useState('assignees')
  const [assigneeValue, setAssigneeValue] = useState('Unassign')
  const [newLabelModal, setNewLabelModal] = useState(false)
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false)
  const [isSnoozeDropdownOpen, setIsSnoozeDropdownOpen] = useState(false)

  const {statusList = []} = useSelector(state => state.emailInbox, _.isEqual)
  const {tagList = []} = useSelector(state => state.settings)

  const {conversationAccess, conversationsPermissions} = useUserPermissions()
  const allowMergeAndSplitFeature = conversationAccess?.mergeAndSplit
  const allowManageConversationRole = conversationsPermissions?.manage_conversation

  const isPreviousConvAvailable = useMemo(() => {
    return conversations.findIndex(con => ConversationId === con.id) > 0
  }, [conversations, ConversationId])

  const isNextConvAvailable = useMemo(() => {
    return conversations.findIndex(con => ConversationId === con.id) < conversations.length
  }, [conversations, ConversationId])

  useHotkeys(
    INBOX_KEYBOARD_SHORTCUTS.DELETE_CONV.key,
    () => allowManageConversationRole && setIsDeleteModalOpen(!isDeleteModalOpen)
  )
  useHotkeys(INBOX_KEYBOARD_SHORTCUTS.TAG_CONV.key, () => setIsTagsDropdownOpen(prevOpen => !prevOpen))
  useHotkeys(INBOX_KEYBOARD_SHORTCUTS.SNOOZE_CONV.key, () => setIsSnoozeDropdownOpen(prevOpen => !prevOpen))
  useHotkeys(
    INBOX_KEYBOARD_SHORTCUTS.GO_TO_PREV_CONV.key,
    () =>
      layout === 'listView' &&
      isPreviousConvAvailable &&
      onClickItem(conversations[conversations.findIndex(con => ConversationId === con.id) - 1].id),
    [layout, isPreviousConvAvailable, conversations, ConversationId]
  )
  useHotkeys(
    INBOX_KEYBOARD_SHORTCUTS.GO_TO_NEXT_CONV.key,
    () =>
      layout === 'listView' &&
      isNextConvAvailable &&
      onClickItem(conversations[conversations.findIndex(con => ConversationId === con.id) + 1].id),
    [layout, isNextConvAvailable, conversations, ConversationId]
  )

  const labelOptions = useMemo(() => {
    return (
      tagList?.map(({id, label, color}) => ({
        value: id,
        key: id,
        text: label,
        label: {
          style: {background: color},
          empty: true,
          circular: true,
        },
      })) ?? []
    )
  }, [tagList])

  const getDefaultAssignee = () => {
    if (CurrentAssigneeOfConversation !== null) {
      setActiveAssigneeType('assignees')
      setAssigneeValue(CurrentAssigneeOfConversation.firstName)
    } else if (currentTeamOfConversation !== null) {
      setActiveAssigneeType('teams')
      setAssigneeValue(currentTeamOfConversation?.name)
    } else {
      setActiveAssigneeType('assignees')
      setAssigneeValue('Unassign')
    }
  }

  useEffect(() => {
    getDefaultAssignee()
  }, [CurrentAssigneeOfConversation, currentTeamOfConversation])

  const listOfLable = useMemo(() => {
    return (
      currentLabelsOfConversation?.map(label => ({
        value: label.id,
        text: label.label,
        color: label.color,
        description: label.description,
      })) ?? []
    )
  }, [currentLabelsOfConversation])

  const onConversationStatusChange = statusTo => {
    const data = {
      statusId: statusTo,
      conversationIds: ConversationId,
    }
    dispatch(updateConversationStatus(data, 'Status'))
  }

  const unSnoozeMail = () => {
    const data = {
      ts: null,
      isSnoozed: false,
    }
    dispatch(snoozeConversation(data, ConversationId))
  }

  const markAsRead = val => {
    const data = {
      conversationIds: ConversationId,
      isRead: val,
    }
    dispatch(updateConversation(data, 'Read'))
  }

  const markAsSpam = val => {
    const conversationIds = []
    conversationIds.push(ConversationId)
    const data = {
      conversationIds,
      isSpam: val,
    }
    dispatch(updateConversationAndRefresh(data, 'Spam Updated'))
  }

  const getStatusId = status => {
    return statusList.find(stat => stat.status === status).id
  }

  const snoozeMail = time => {
    const data = {
      ts: time,
      isSnoozed: true,
    }
    dispatch(snoozeConversation(data, ConversationId))
  }

  const renderListOfMailInbox = () => {
    const finalListOfInbox = mailboxes || []

    const listOfInboxExceptOne = finalListOfInbox.filter(inbox => inbox.id !== mailboxId)

    const listOfVerifiedInbox = listOfInboxExceptOne.filter(inbox =>
      inbox.type === INBOX_TYPES.EMAIL ? inbox.emailVerified : true
    )

    return listOfVerifiedInbox[0] ? (
      listOfVerifiedInbox.map(inbox => (
        <VmoDropdownItem
          key={inbox.id}
          disabled={!allowManageConversationRole}
          onClick={() => {
            if (!allowManageConversationRole) return
            const data = {
              conversationIds: [ConversationId],
              mailboxId: inbox.id,
            }
            dispatch(moveConversationTo(data))
          }}
        >
          {inbox.name}
        </VmoDropdownItem>
      ))
    ) : (
      <p className="p-2">No Data Available</p>
    )
  }

  const renderConversationOptions = () => {
    return (
      <>
        {layout === 'listView' && (
          <VmoTooltip
            trigger={
              <VmoButton className="task-btn-inbox" icon onClick={() => navigate(`/inbox`)}>
                <FiArrowLeft id="backButton" />
              </VmoButton>
            }
            content="Back"
          />
        )}
        {get(['isTrashed'], conversation) && (
          <VmoTooltip
            trigger={
              <VmoButton
                icon
                onClick={() => {
                  if (!allowManageConversationRole) return

                  const conversationIds = []
                  conversationIds.push(ConversationId)
                  const data = {
                    conversationIds,
                    isTrashed: false,
                  }
                  dispatch(updateConversationAndRefresh(data, 'Conversation Restored'))
                }}
              >
                <MdSettingsBackupRestore />
              </VmoButton>
            }
            content={
              allowManageConversationRole
                ? 'Restore Conversation'
                : getPermissionTooltipContent({roleAccess: allowManageConversationRole})
            }
          />
        )}

        <VmoTooltip
          content={
            allowManageConversationRole
              ? get(['status'], CurrentStatusOfConversation, 'open') !== 'closed'
                ? 'Mark as Close'
                : 'Mark as Open'
              : getPermissionTooltipContent({roleAccess: allowManageConversationRole})
          }
          trigger={
            <VmoButton
              icon={<FiCheck />}
              color={
                get(['status'], CurrentStatusOfConversation, 'open') !== 'closed' ? 'ml-0' : 'primary theme-clr ml-0'
              }
              onClick={() =>
                allowManageConversationRole &&
                onConversationStatusChange(
                  get(['status'], CurrentStatusOfConversation, 'open') !== 'closed'
                    ? getStatusId('Closed')
                    : getStatusId('Open')
                )
              }
              size="mini"
              position="top center"
            />
          }
        />
        <VmoTooltip
          content={
            allowManageConversationRole
              ? conversation.isStarred
                ? 'Remove Favorite'
                : 'Add as Favorite'
              : getPermissionTooltipContent({roleAccess: allowManageConversationRole})
          }
          trigger={
            <VmoRating
              className="icon button mr-0"
              icon="star"
              rating={conversation.isStarred ? 1 : 0}
              size="large"
              onClick={() =>
                allowManageConversationRole &&
                dispatch(
                  updateConversation(
                    {
                      isStarred: !conversation.isStarred,
                      conversationIds: ConversationId,
                    },
                    'Favourite'
                  )
                )
              }
            />
          }
        />

        {isSnoozed !== null ? (
          <VmoTooltip
            content={
              allowManageConversationRole
                ? moment(isSnoozed).endOf().fromNow()
                : getPermissionTooltipContent({roleAccess: allowManageConversationRole})
            }
            trigger={
              <VmoDropdown
                className="active icon button"
                direction="right"
                icon={
                  <FiClock
                    id="TooltipSnooze"
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      if (allowManageConversationRole) {
                        unSnoozeMail()
                      }
                    }}
                  />
                }
              />
            }
          />
        ) : (
          <VmoTooltip
            content={
              allowManageConversationRole
                ? 'Snooze'
                : getPermissionTooltipContent({roleAccess: allowManageConversationRole})
            }
            trigger={
              <VmoDropdown
                className="icon button"
                direction="right"
                icon={<FiClock id="TooltipSnooze" />}
                onClick={() => setIsSnoozeDropdownOpen(prevOpen => !prevOpen)}
                open={isSnoozeDropdownOpen}
              >
                <VmoDropdownMenu>
                  <VmoDropdownItem
                    disabled={!allowManageConversationRole}
                    onClick={() => {
                      if (!allowManageConversationRole) return
                      const time = new Date()
                      time.setHours(time.getHours() + 1)
                      const date = new Date(time)
                      snoozeMail(date)
                    }}
                  >
                    1 hour
                  </VmoDropdownItem>
                  <VmoDropdownItem
                    disabled={!allowManageConversationRole}
                    onClick={() => {
                      if (!allowManageConversationRole) return
                      const time = new Date()
                      time.setHours(time.getHours() + 2)
                      const date = new Date(time)
                      snoozeMail(date)
                    }}
                  >
                    2 hours
                  </VmoDropdownItem>
                  <VmoDropdownItem
                    disabled={!allowManageConversationRole}
                    onClick={() => {
                      if (!allowManageConversationRole) return
                      const time = new Date()
                      time.setHours(time.getHours() + 4)
                      const date = new Date(time)
                      snoozeMail(date)
                    }}
                  >
                    4 hours
                  </VmoDropdownItem>
                  <VmoDropdownItem
                    disabled={!allowManageConversationRole}
                    onClick={() => {
                      if (!allowManageConversationRole) return
                      const time = new Date()
                      time.setHours(time.getHours() + 8)
                      const date = new Date(time)
                      snoozeMail(date)
                    }}
                  >
                    8 hours
                  </VmoDropdownItem>
                  <VmoDropdownItem
                    disabled={!allowManageConversationRole}
                    onClick={() => {
                      if (!allowManageConversationRole) return
                      setTimePicker(true)
                      setSnoozeDatePickerTime(new Date())
                    }}
                  >
                    Pick Date and Time
                  </VmoDropdownItem>
                </VmoDropdownMenu>
              </VmoDropdown>
            }
          />
        )}

        <VmoTooltip
          content={
            allowManageConversationRole
              ? 'Move To'
              : getPermissionTooltipContent({roleAccess: allowManageConversationRole})
          }
          trigger={
            <VmoDropdown className="icon button" icon={<FiFolder id="TooltipFolderInbox" />}>
              <VmoDropdownMenu>
                <VmoDropdownHeader>Move to inbox</VmoDropdownHeader>
                {renderListOfMailInbox()}
              </VmoDropdownMenu>
            </VmoDropdown>
          }
        />

        <VmoTooltip
          content="More"
          trigger={
            <VmoDropdown className="icon button actMoreBtn" icon={<FiMoreVertical id="TooltipMore" />}>
              <VmoDropdownMenu>
                {!CurrentReadStatusOfConversation ? (
                  <VmoDropdownItem onClick={() => allowManageConversationRole && markAsRead(true)}>
                    <FiCheck id="TooltipMore" />
                    Mark as a Read
                    {!allowManageConversationRole && (
                      <span style={{marginLeft: 'auto'}}>
                        <PermissionIcon isRoleAccessDenied={!allowManageConversationRole} />
                      </span>
                    )}
                  </VmoDropdownItem>
                ) : (
                  <VmoDropdownItem onClick={() => allowManageConversationRole && markAsRead(false)}>
                    <FiRotateCcw id="TooltipMore" />
                    Mark as a Unread
                    {!allowManageConversationRole && (
                      <span style={{marginLeft: 'auto'}}>
                        <PermissionIcon isRoleAccessDenied={!allowManageConversationRole} />
                      </span>
                    )}
                  </VmoDropdownItem>
                )}
                <VmoDropdownItem
                  onClick={() =>
                    allowMergeAndSplitFeature && allowManageConversationRole && setMergeModalOpen(!isMergeModalOpen)
                  }
                >
                  <FiGitMerge id="TooltipMore" /> Merge Mails
                  {(!allowMergeAndSplitFeature || !allowManageConversationRole) && (
                    <span style={{marginLeft: 'auto'}}>
                      <PermissionIcon
                        isFeatureAccessDenied={!allowMergeAndSplitFeature}
                        isRoleAccessDenied={!allowManageConversationRole}
                      />
                    </span>
                  )}
                </VmoDropdownItem>
                <VmoDropdownItem onClick={() => allowManageConversationRole && setOverDue(!overDue)}>
                  <FiClock /> Overdue
                  {!allowManageConversationRole && (
                    <span style={{marginLeft: 'auto'}}>
                      <PermissionIcon isRoleAccessDenied={!allowManageConversationRole} />
                    </span>
                  )}
                </VmoDropdownItem>
                <VmoDropdownItem
                  onClick={() => allowManageConversationRole && setIsDeleteModalOpen(!isDeleteModalOpen)}
                >
                  <FiTrash2 id="TooltipMore" /> Delete
                  {!allowManageConversationRole && (
                    <span style={{marginLeft: 'auto'}}>
                      <PermissionIcon isRoleAccessDenied={!allowManageConversationRole} />
                    </span>
                  )}
                </VmoDropdownItem>
                <VmoDivider />
                <ReactToPrint
                  trigger={() => (
                    <VmoDropdownItem>
                      <FiPrinter id="TooltipMore" /> Print
                    </VmoDropdownItem>
                  )}
                  content={() => emailBodyContentRef.current}
                />
                <VmoDropdownItem>
                  <Link to={`/conversation/${ConversationId}`} target="_blank">
                    <FiExternalLink id="TooltipMore" style={{color: '#007bf2'}} />
                    Open in a New Window
                  </Link>
                </VmoDropdownItem>
                {!CurrentSpamStatusOfConversation ? (
                  <VmoDropdownItem onClick={() => allowManageConversationRole && markAsSpam(true)}>
                    <FiAlertCircle id="TooltipMore" /> Report as Spam
                    {!allowManageConversationRole && (
                      <span style={{marginLeft: 'auto'}}>
                        <PermissionIcon isRoleAccessDenied={!allowManageConversationRole} />
                      </span>
                    )}
                  </VmoDropdownItem>
                ) : (
                  <VmoDropdownItem onClick={() => allowManageConversationRole && markAsSpam(false)}>
                    <FiAlertCircle id="TooltipMore" /> Report not Spam
                    {!allowManageConversationRole && (
                      <span style={{marginLeft: 'auto'}}>
                        <PermissionIcon isRoleAccessDenied={!allowManageConversationRole} />
                      </span>
                    )}
                  </VmoDropdownItem>
                )}
              </VmoDropdownMenu>
            </VmoDropdown>
          }
        />
      </>
    )
  }

  const onDeleteConversation = () => {
    const conversationIds = []
    conversationIds.push(ConversationId)

    if (isTrashedConversation === true) {
      const data = {
        conversationIds,
      }
      dispatch(permanentDeleteConversation(data))
      setIsDeleteModalOpen(false)
    } else {
      const data = {
        conversationIds,
        isTrashed: true,
      }
      dispatch(updateConversationAndRefresh(data, 'Moved to Trash'))
      setIsDeleteModalOpen(false)
    }
  }

  return (
    <>
      <div className="headerBar">
        {!ticketNo && (
          <VmoTooltip
            trigger={
              <VmoButton
                className="task-btn-inbox"
                icon
                onClick={() => {
                  navigate(`/inbox`)
                }}
              >
                <FiArrowLeft id="backButton" />
              </VmoButton>
            }
            content="Back"
          />
        )}
        {ticketNo && (
          <div className="actBtn">
            {renderConversationOptions()}
            {timeTrackingData.isTimeTracking && (
              <div className="timeTrack">
                <VmoButton
                  onClick={() => {
                    if (!allowManageConversationRole) return
                    setTimeTrackingCounterStatus(timeTrackingCounterStatus === 'running' ? 'paused' : 'running')
                    if (!timeTrackingData.isAutoForConversation)
                      dispatch(
                        submitTimeTrackingManual({
                          conversationId: ConversationId,
                          status: timeTrackingCounterStatus === 'running' ? 'paused' : 'running',
                        })
                      )
                  }}
                >
                  {timeTrackingCounterStatus === 'running' ? (
                    <VmoTooltip
                      content={
                        allowManageConversationRole
                          ? 'Stop Time Tracking'
                          : getPermissionTooltipContent({roleAccess: allowManageConversationRole})
                      }
                      trigger={<VmoIcon name="pause" />}
                    />
                  ) : (
                    <VmoTooltip
                      content={
                        allowManageConversationRole
                          ? 'Start Time Tracking'
                          : getPermissionTooltipContent({roleAccess: allowManageConversationRole})
                      }
                      trigger={<VmoIcon name="play" />}
                    />
                  )}
                </VmoButton>
                {`${moment('2000-01-01 00:00:00').add(moment.duration(timeTrackingTimer)).format('HH:mm:ss')}`}
                <VmoButton
                  onClick={() => {
                    if (!allowManageConversationRole) return
                    if (timeTrackingData.isAutoForConversation) {
                      setTimeTrackingCounterStatus('running')
                    } else {
                      setTimeTrackingCounterStatus('completed')
                    }
                    dispatch(
                      submitTimeTrackingManual({
                        conversationId: ConversationId,
                        status: 'completed',
                        duration: 0,
                      })
                    )
                    setTimeTrackingTimer(0)
                  }}
                >
                  <VmoTooltip
                    content={
                      allowManageConversationRole
                        ? 'Reset Time Tracking'
                        : getPermissionTooltipContent({roleAccess: allowManageConversationRole})
                    }
                    trigger={<VmoIcon name="redo" />}
                  />
                </VmoButton>
              </div>
            )}
          </div>
        )}
        {ticketNo && (
          <div className="mailActivity">
            <VmoTooltip
              trigger={
                <VmoDropdown
                  button
                  className="inboxtour__step-three"
                  options={statusList.map(status => ({
                    key: status.id,
                    value: status.id,
                    text: startCase(status.status),
                    disabled: !allowManageConversationRole,
                  }))}
                  header="Status"
                  key={get(['id'], CurrentStatusOfConversation)}
                  defaultValue={get(['id'], CurrentStatusOfConversation)}
                  onChange={(e, {value}) => {
                    if (allowManageConversationRole) {
                      onConversationStatusChange(value)
                    }
                  }}
                />
              }
              content={
                allowManageConversationRole
                  ? 'Status'
                  : getPermissionTooltipContent({roleAccess: allowManageConversationRole})
              }
            />

            <VmoTooltip
              trigger={
                <VmoDropdown
                  button
                  value={CurrentPriorityOfConversation}
                  options={priorityList.map(priority => ({
                    key: priority.key,
                    value: priority.value,
                    text: startCase(priority.text),
                    disabled: !allowManageConversationRole,
                  }))}
                  header="Priority"
                  onChange={(e, value) => {
                    if (allowManageConversationRole) {
                      const data = {
                        priority: value.value,
                        conversationIds: ConversationId,
                      }
                      dispatch(updateConversationPriority(data, 'Priority'))
                    }
                  }}
                />
              }
              content={
                allowManageConversationRole
                  ? 'Priority'
                  : getPermissionTooltipContent({roleAccess: allowManageConversationRole})
              }
            />

            <VmoTooltip
              trigger={
                <VmoDropdown
                  button
                  text={assigneeValue}
                  className="assignee-dropdown-main inboxtour__step-two"
                  onBlur={() => getDefaultAssignee()}
                >
                  <VmoDropdownMenu>
                    <h5>Conversation assign to :</h5>
                    <div className="action">
                      <VmoButton
                        className={activeAssigneeType === 'assignees' ? 'active' : ''}
                        onClick={e => {
                          e.stopPropagation()
                          setActiveAssigneeType('assignees')
                          // setAssigneeValue('Users')
                        }}
                      >
                        <VmoIcon name="user" />
                        Users
                      </VmoButton>
                      <VmoButton
                        className={activeAssigneeType === 'teams' ? 'active' : ''}
                        onClick={e => {
                          e.stopPropagation()
                          setActiveAssigneeType('teams')
                          // setAssigneeValue('Teams')
                        }}
                      >
                        <VmoIcon name="users" />
                        Teams
                      </VmoButton>
                    </div>
                    {assigneeOptions[activeAssigneeType]?.length > 0 ? (
                      assigneeOptions[activeAssigneeType]?.map(({value, text}) => (
                        <VmoDropdownItem
                          key={value}
                          disabled={text === 'Teams' || !allowManageConversationRole}
                          className={`${
                            activeAssigneeType === 'teams' ? '' : assigneeValue === text ? 'active' : ''
                          } assignee-dropdown`}
                          onClick={() => {
                            if (!allowManageConversationRole) return
                            if (value === 'unassign') {
                              return dispatch(
                                updateConversation(
                                  {
                                    unassign: true,
                                    conversationIds: ConversationId,
                                  },
                                  'Assignee'
                                )
                              )
                            }

                            let data = {}
                            if (value?.isTeam) {
                              data = {
                                conversationIds: ConversationId,
                                teamId: value.value,
                              }
                            } else {
                              data = {
                                conversationIds: ConversationId,
                                assigneeId: value,
                              }
                            }

                            dispatch(updateConversation(data, 'Assignee'))
                          }}
                        >
                          {value === 'unassign' ? (
                            <VmoIcon name="ban" />
                          ) : value?.isTeam ? (
                            <VmoIcon name="users" />
                          ) : text === 'Teams' ? null : (
                            <VmoIcon name="user" />
                          )}
                          {text === 'Teams' ? <p>No Teams Available</p> : text}
                        </VmoDropdownItem>
                      ))
                    ) : (
                      <VmoDropdownItem disabled>No Team Available</VmoDropdownItem>
                    )}
                  </VmoDropdownMenu>
                </VmoDropdown>
              }
              content={
                allowManageConversationRole
                  ? 'User'
                  : getPermissionTooltipContent({roleAccess: allowManageConversationRole})
              }
            />

            {layout === 'listView' && (
              <VmoButtonGroup>
                <VmoButton
                  icon
                  className="task-btn-inbox"
                  disabled={!isPreviousConvAvailable}
                  onClick={() =>
                    onClickItem(conversations[conversations.findIndex(con => ConversationId === con.id) - 1].id)
                  }
                >
                  <FiChevronLeft />
                </VmoButton>
                <VmoButton
                  className="task-btn-inbox"
                  icon
                  disabled={!isNextConvAvailable}
                  onClick={() =>
                    onClickItem(conversations[conversations.findIndex(con => ConversationId === con.id) + 1].id)
                  }
                >
                  <FiChevronRight />
                </VmoButton>
              </VmoButtonGroup>
            )}
          </div>
        )}
      </div>
      <div className="SubjectBar">
        <div className="subject">
          <h5>
            <VmoLabel horizontal>#{ticketNo}</VmoLabel> {conversationSubject}
          </h5>
          <div className="d-flex" style={{columnGap: 'var(--gap-medium)'}}>
            <VmoChipInput
              labelProps={{
                basic: true,
                as: 'a',
                className: 'vmo-tag',
              }}
              className="subtag"
              defaultValue={listOfLable.map(tag => {
                return {
                  key: tag.value,
                  id: tag.value,
                  text: <>{tag.text}</>,
                  color: tag.color,
                }
              })}
              onChange={value => {
                if (!allowManageConversationRole) return
                const valueIds = value.map(tag => tag.id)

                dispatch(
                  updateConversation({
                    conversationIds: ConversationId,
                    labels: valueIds,
                  })
                )
              }}
              hideInputField
            />

            <VmoCustomDropdown
              options={labelOptions}
              multiple
              dropdownProps={{
                onClick: () => setIsTagsDropdownOpen(prevOpen => !prevOpen),
                open: isTagsDropdownOpen,
                icon: <FiTag id="TooltipTagInbox" />,
                closeOnChange: false,
                className: 'm-0 icon button tag-dropdown',
                direction: 'left',
              }}
              tooltip
              tooltipContent={
                allowManageConversationRole
                  ? 'Tags'
                  : getPermissionTooltipContent({roleAccess: allowManageConversationRole})
              }
              menu={{scrolling: true}}
              input={{
                icon: 'search',
                iconPosition: 'left',
                className: 'search',
                placeholder: 'search..',
              }}
              activeIndicator
              item={{disabled: !allowManageConversationRole}}
              value={listOfLable.map(labels => labels.value)}
              onClick={value => {
                if (!allowManageConversationRole) return
                dispatch(
                  updateConversation({
                    conversationIds: ConversationId,
                    labels: value,
                  })
                )
              }}
              extraAction={{
                content: '+ Add Tag',
                onClick: () => allowManageConversationRole && setNewLabelModal(true),
                disabled: !allowManageConversationRole,
              }}
            />

            <VmoTooltip
              size="tiny"
              content={`${get(['contact', 'firstName'], conversation, 'Unknown')}`}
              position="left center"
              trigger={
                <VmoButton
                  className="icon m-0 inboxtour__step-four"
                  onClick={() => {
                    setCustomerProfile(!customerProfile)
                    const oldSupport = JSON.parse(window.localStorage.getItem('support') || '{}')
                    window.localStorage.setItem(
                      'support',
                      JSON.stringify({
                        ...oldSupport,
                        sidebarToggle: !customerProfile,
                      })
                    )
                  }}
                  disabled={!get(['contact', 'id'], conversation)}
                >
                  <BsFillPersonLinesFill profile={get(['contact'], conversation)} />
                </VmoButton>
              }
            />
          </div>
        </div>
      </div>

      {timePicker && (
        <SnoozeConversationModal
          timePicker={timePicker}
          setTimePicker={setTimePicker}
          snoozeDatePickerTime={snoozeDatePickerTime}
          setSnoozeDatePickerTime={setSnoozeDatePickerTime}
          ConversationId={ConversationId}
        />
      )}

      {isMergeModalOpen && (
        <MergeConversationModal
          isMergeModalOpen={isMergeModalOpen}
          setMergeModalOpen={setMergeModalOpen}
          conversationSubject={conversationSubject}
          ConversationId={ConversationId}
          conversations={conversations}
          conversation={conversation}
        />
      )}

      {overDue && (
        <OverdueModal
          isOpen={overDue}
          setIsOpen={setOverDue}
          onSuccess={value => {
            const data = {
              conversationIds: ConversationId,
              dueDate: value,
            }
            dispatch(updateConversation(data, 'Overdue'))
          }}
        />
      )}

      {isDeleteModalOpen && (
        <EmailDeleteModal
          isModalOpen={isDeleteModalOpen}
          setIsModalOpen={setIsDeleteModalOpen}
          onDeleteConversation={onDeleteConversation}
          isTrashedConversation={isTrashedConversation}
        />
      )}

      {newLabelModal && (
        <CreateLabel
          isModalOpen={newLabelModal}
          setIsModalOpen={setNewLabelModal}
          createMethod={data => dispatch(createTagSettings(data))}
          conversationIds={[ConversationId]}
        />
      )}
    </>
  )
}

export default EmailBodyHeader
